import { useState } from 'react';

// Team colors for fallback badges
const TEAM_COLORS = {
  // AFL Teams
  "Adelaide Crows": { primary: "#002B5C", secondary: "#FFD200" },
  "Brisbane Lions": { primary: "#A30046", secondary: "#FFB81C" },
  "Carlton": { primary: "#0E1E2D", secondary: "#FFFFFF" },
  "Collingwood": { primary: "#000000", secondary: "#FFFFFF" },
  "Essendon": { primary: "#CC2031", secondary: "#000000" },
  "Fremantle": { primary: "#2A1A54", secondary: "#FFFFFF" },
  "Geelong Cats": { primary: "#001F3D", secondary: "#FFFFFF" },
  "Gold Coast Suns": { primary: "#F47920", secondary: "#FFD200" },
  "GWS Giants": { primary: "#F15A22", secondary: "#000000" },
  "Hawthorn": { primary: "#4D2004", secondary: "#FFB81C" },
  "Melbourne": { primary: "#0F1131", secondary: "#CC2031" },
  "North Melbourne": { primary: "#003D7C", secondary: "#FFFFFF" },
  "Port Adelaide": { primary: "#008AAB", secondary: "#000000" },
  "Richmond": { primary: "#000000", secondary: "#FED102" },
  "St Kilda": { primary: "#ED1B2F", secondary: "#000000" },
  "Sydney Swans": { primary: "#ED171F", secondary: "#FFFFFF" },
  "West Coast Eagles": { primary: "#062A78", secondary: "#FFD200" },
  "Western Bulldogs": { primary: "#0039A6", secondary: "#CC2031" },
  // NRL Teams
  "Brisbane Broncos": { primary: "#6F263D", secondary: "#FFB81C" },
  "Canterbury Bulldogs": { primary: "#005BAC", secondary: "#FFFFFF" },
  "Canberra Raiders": { primary: "#00AB51", secondary: "#FFFFFF" },
  "Cronulla Sharks": { primary: "#00AEEF", secondary: "#000000" },
  "Dolphins": { primary: "#E4002B", secondary: "#FFD200" },
  "Gold Coast Titans": { primary: "#0098D3", secondary: "#FFD200" },
  "Manly Sea Eagles": { primary: "#6F263D", secondary: "#FFFFFF" },
  "Melbourne Storm": { primary: "#461D7C", secondary: "#FFD200" },
  "Newcastle Knights": { primary: "#003D7C", secondary: "#CC2031" },
  "New Zealand Warriors": { primary: "#000000", secondary: "#CC2031" },
  "North Queensland Cowboys": { primary: "#002B5C", secondary: "#FFD200" },
  "Parramatta Eels": { primary: "#004B98", secondary: "#FFD200" },
  "Penrith Panthers": { primary: "#000000", secondary: "#FF6B00" },
  "South Sydney Rabbitohs": { primary: "#00843D", secondary: "#CC2031" },
  "St George Illawarra Dragons": { primary: "#E4002B", secondary: "#FFFFFF" },
  "Sydney Roosters": { primary: "#0033A0", secondary: "#CC2031" },
  "Wests Tigers": { primary: "#F7941E", secondary: "#000000" },
};

// Get team initials (up to 3 characters)
function getTeamInitials(teamName) {
  const words = teamName.split(' ');
  if (words.length === 1) return words[0].substring(0, 3).toUpperCase();
  if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase();
  // For 3+ words, use first letter of key words
  return words.slice(0, 3).map(w => w[0]).join('').toUpperCase();
}

// Uploaded logo overrides - these take priority over API logos
const UPLOADED_LOGOS = {
  // NRL Teams (all 17)
  "Brisbane Broncos": "/logos/brisbane_broncos.png",
  "Wests Tigers": "/logos/wests_tigers.png",
  "Sydney Roosters": "/logos/sydney_roosters.png",
  "St George Illawarra Dragons": "/logos/st_george_illawarra_dragons.png",
  "South Sydney Rabbitohs": "/logos/south_sydney_rabbitohs.png",
  "Penrith Panthers": "/logos/penrith_panthers.png",
  "Parramatta Eels": "/logos/parramatta_eels.png",
  "North Queensland Cowboys": "/logos/north_queensland_cowboys.png",
  "Newcastle Knights": "/logos/newcastle_knights.png",
  "New Zealand Warriors": "/logos/new_zealand_warriors.png",
  "Melbourne Storm": "/logos/melbourne_storm.png",
  "Manly Sea Eagles": "/logos/manly_sea_eagles.png",
  "Gold Coast Titans": "/logos/gold_coast_titans.png",
  "Cronulla Sharks": "/logos/cronulla_sharks.png",
  "Canberra Raiders": "/logos/canberra_raiders.png",
  "Canterbury Bulldogs": "/logos/canterbury_bulldogs.png",
  "Dolphins": "/logos/dolphins.png",
  // AFL Teams
  "Western Bulldogs": "/logos/western_bulldogs.png",
  "GWS Giants": "/logos/gws_giants.png",
  "North Melbourne": "/logos/north_melbourne.png",
  "Gold Coast Suns": "/logos/gold_coast_suns.png",
  "Brisbane Lions": "/logos/brisbane_lions.png",
  "Collingwood": "/logos/collingwood.png",
  "Port Adelaide": "/logos/port_adelaide.png",
  "Adelaide Crows": "/logos/adelaide_crows.png",
  "Carlton": "/logos/carlton.png",
  "Fremantle": "/logos/fremantle.png",
  "Melbourne": "/logos/melbourne.png",
  "Richmond": "/logos/richmond.png",
  "Hawthorn": "/logos/hawthorn.png",
  "Essendon": "/logos/essendon.png",
  "Geelong Cats": "/logos/geelong_cats.png",
  "St Kilda": "/logos/st_kilda.png",
  "West Coast Eagles": "/logos/west_coast_eagles.png",
  "Sydney Swans": "/logos/sydney_swans.png",
};

export default function TeamLogo({ teamName, logoUrl, size = 48, className = "" }) {
  const [imgError, setImgError] = useState(false);
  const colors = TEAM_COLORS[teamName] || { primary: "#333333", secondary: "#FFFFFF" };
  const initials = getTeamInitials(teamName);
  
  const sizeClass = size <= 32 ? 'w-8 h-8 text-xs' : size <= 48 ? 'w-12 h-12 text-sm' : 'w-16 h-16 text-base';
  
  // Check for uploaded logo first, then API logo
  const effectiveUrl = UPLOADED_LOGOS[teamName] || logoUrl;
  
  // If no URL or image failed, show colored badge with initials
  if (!effectiveUrl || imgError) {
    return (
      <div 
        className={`${sizeClass} rounded-sm flex items-center justify-center font-bold flex-shrink-0 ${className}`}
        style={{ 
          backgroundColor: colors.primary,
          color: colors.secondary,
          border: `2px solid ${colors.secondary}`
        }}
        title={teamName}
      >
        {initials}
      </div>
    );
  }
  
  return (
    <img 
      src={effectiveUrl}
      alt={teamName}
      className={`${sizeClass} object-contain flex-shrink-0 ${className}`}
      style={{ background: 'transparent' }}
      onError={() => setImgError(true)}
    />
  );
}

export { TEAM_COLORS, getTeamInitials };
